import { GitBranch } from "lucide-react";
import type { ReactNode } from "react";
import { personLabel } from "../../lib/people";
import { type IssuesMeta, type IssueView, WAITING_ON } from "../../types";
import { PRIORITY_LABELS, PriorityIcon, SLA_LABELS, StatusDisc, TypeIcon, WAITING_LABELS } from "./meta";

interface Group {
  key: string;
  label: string;
  icon?: ReactNode;
  items: IssueView[];
  order: number;
}

export function groupIssues(items: IssueView[], groupBy: string, meta: IssuesMeta | undefined): Group[] {
  const groups = new Map<string, Group>();
  const push = (key: string, label: string, order: number, issue: IssueView, icon?: ReactNode) => {
    const group = groups.get(key) ?? { key, label, icon, items: [], order };
    group.items.push(issue);
    groups.set(key, group);
  };
  for (const issue of items) {
    switch (groupBy) {
      case "type": {
        const type = meta?.types.find((t) => t.key === issue.typeKey);
        push(
          issue.typeKey,
          type?.name ?? issue.typeKey,
          type?.position ?? 99,
          issue,
          <TypeIcon typeKey={issue.typeKey} color={type?.color} />,
        );
        break;
      }
      case "priority":
        push(
          String(issue.priority),
          PRIORITY_LABELS[issue.priority] ?? "Unknown",
          issue.priority === 0 ? 5 : issue.priority,
          issue,
          <PriorityIcon priority={issue.priority} />,
        );
        break;
      case "repo": {
        const index = meta?.repos.findIndex((repo) => repo.key === issue.repoKey) ?? -1;
        const repo = index >= 0 ? meta?.repos[index] : undefined;
        push(
          issue.repoKey,
          repo?.name ?? issue.repoKey,
          index >= 0 ? index : 99,
          issue,
          <GitBranch className="size-3.5 text-muted-foreground" />,
        );
        break;
      }
      case "team":
        if (issue.teams.length === 0) push("none", "No team", 99, issue);
        for (const team of issue.teams)
          push(
            team.key,
            team.name,
            0,
            issue,
            <span className="size-2.5 rounded-sm" style={{ backgroundColor: team.color }} />,
          );
        break;
      case "label":
        if (issue.labels.length === 0) push("none", "No label", 99, issue);
        for (const label of issue.labels)
          push(
            label.name,
            label.name,
            0,
            issue,
            <span className="size-2.5 rounded-full" style={{ backgroundColor: label.color }} />,
          );
        break;
      case "reviewer":
        push(
          issue.reviewer?.id ?? "none",
          issue.reviewer ? personLabel(issue.reviewer) : "No reviewer",
          issue.reviewer ? 0 : 99,
          issue,
        );
        break;
      case "reporter":
        push(
          issue.reporter?.id ?? "none",
          issue.reporter ? personLabel(issue.reporter) : "No reporter",
          issue.reporter ? 0 : 99,
          issue,
        );
        break;
      case "sla":
        push(
          issue.slaStatus,
          SLA_LABELS[issue.slaStatus],
          ["breached", "at_risk", "on_track", "met", "none"].indexOf(issue.slaStatus),
          issue,
        );
        break;
      case "waitingOn":
        push(
          issue.waitingOn ?? "none",
          issue.waitingOn ? WAITING_LABELS[issue.waitingOn] : "Nobody needed",
          issue.waitingOn ? WAITING_ON.indexOf(issue.waitingOn) : WAITING_ON.length,
          issue,
        );
        break;
      default: {
        const status = meta?.statuses.find((s) => s.key === issue.statusKey);
        push(
          issue.statusKey,
          status?.name ?? issue.statusKey,
          status?.position ?? 99,
          issue,
          <StatusDisc category={status?.category} color={status?.color} />,
        );
      }
    }
  }
  return [...groups.values()].sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
}
